import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useClearFraud, useConfirmFraud, useDeleteRecording } from "../api/recordings";
import { errorMessage } from "../api/client";
import ConfirmDialog from "./ConfirmDialog";
import type { RecordingDetail } from "../api/types";
import { STATUS_META } from "../lib/grade";

// Sticky bottom action bar for recordings in a fraud state. `potential_fraud`: the reviewer
// either confirms (terminal `fraud`) or clears it (back through the pipeline with the
// anti-fraud stage skipped). `fraud`: read-only status + the decision note. Hard delete is
// kept here too so a confirmed fraud can be purged like any other recording.
export default function FraudBar({ rec }: { rec: RecordingDetail }) {
  const [note, setNote] = useState("");
  const confirm = useConfirmFraud(rec.id);
  const clear = useClearFraud(rec.id);
  const del = useDeleteRecording(rec.id);
  const navigate = useNavigate();

  const decidable = rec.status === "potential_fraud";
  const busy = confirm.isPending || clear.isPending || del.isPending;
  const meta = STATUS_META[rec.status];
  const body = { note: note || null };

  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [confirmingFraud, setConfirmingFraud] = useState(false);
  const onDelete = () => {
    del.mutate(undefined, {
      onSuccess: () => navigate("/fraud"),
      onSettled: () => setConfirmingDelete(false),
    });
  };
  const onConfirmFraud = () => {
    confirm.mutate(body, { onSettled: () => setConfirmingFraud(false) });
  };

  const err =
    (confirm.isError && errorMessage(confirm.error)) ||
    (clear.isError && errorMessage(clear.error)) ||
    (del.isError && errorMessage(del.error)) ||
    null;

  const audit = rec.fraud;
  const auditLine =
    rec.status === "fraud"
      ? `confirmed by ${audit?.confirmed_by ?? "a reviewer"}${audit?.confirm_note ? ` — “${audit.confirm_note}”` : ""}`
      : null;

  return (
    <div className="action-bar fraud-bar">
      <div className="action-bar-inner">
        <div className="action-left">
          <span className="status-badge" style={{ ["--sc" as string]: meta?.color ?? "#6e7681" }}>
            <span className="status-dot" />
            {meta?.label ?? rec.status}
          </span>
          {auditLine && <span className="muted small action-decision">{auditLine}</span>}
          {decidable && (
            <span className="muted small action-decision">
              Processing was halted before the heavy stages. “Not fraud” resumes it.
            </span>
          )}
        </div>

        {decidable && (
          <input
            className="note-input"
            placeholder="Decision note (optional)…"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        )}

        <div className="action-buttons">
          <button className="btn danger" disabled={busy} onClick={() => setConfirmingDelete(true)}>
            {del.isPending ? "Deleting…" : "Delete"}
          </button>
          {decidable && (
            <>
              <button className="btn approve" disabled={busy} onClick={() => clear.mutate(body)}>
                {clear.isPending ? "Resuming…" : "Not fraud — resume processing"}
              </button>
              <button className="btn reject" disabled={busy} onClick={() => setConfirmingFraud(true)}>
                {confirm.isPending ? "Confirming…" : "Confirm fraud"}
              </button>
            </>
          )}
        </div>
      </div>
      {err && <p className="error action-error">{err}</p>}
      <ConfirmDialog
        open={confirmingFraud}
        title="Confirm this recording as fraud?"
        confirmLabel={confirm.isPending ? "Confirming…" : "Confirm fraud"}
        tone="danger"
        busy={confirm.isPending}
        onConfirm={onConfirmFraud}
        onCancel={() => setConfirmingFraud(false)}
      >
        <p>
          The recording becomes <b>fraud</b>: it will never be processed, reviewed or paid. The
          contributor's app shows it as rejected.
        </p>
        <p>Your name and note are recorded with the decision.</p>
      </ConfirmDialog>
      <ConfirmDialog
        open={confirmingDelete}
        title="Permanently delete this recording?"
        confirmLabel={del.isPending ? "Deleting…" : "Delete recording"}
        tone="danger"
        busy={del.isPending}
        onConfirm={onDelete}
        onCancel={() => setConfirmingDelete(false)}
      >
        <p>
          The database entry <b>and</b> the stored video/artifacts (Oracle bucket + server
          files) will be removed.
        </p>
        <p>This cannot be undone.</p>
      </ConfirmDialog>
    </div>
  );
}
